import React, { useState, useRef, useEffect, useImperativeHandle, useCallback, forwardRef } from 'react';

import { Button } from 'devextreme-react/button';
import { LoadIndicator } from 'devextreme-react/load-indicator';
import AntLocalization from '../utils/AntLocalization';

export default forwardRef(function (props, ref) {
    const [processing, setProcessing] = useState(false);
    const inFlight = useRef(false);
    const autoStopTimer = useRef(null);
    const mounted = useRef(true);

    const reset = useCallback(() => {
        if (autoStopTimer.current) clearTimeout(autoStopTimer.current);
        autoStopTimer.current = null;
        inFlight.current = false;
        if (mounted.current) setProcessing(false);
    }, []);

    useEffect(() => {
        mounted.current = true;
        return () => {
            mounted.current = false;
            if (autoStopTimer.current) clearTimeout(autoStopTimer.current);
        };
    }, []);

    const handleOnClick = useCallback(
        (e) => {
            e.event?.stopPropagation();
            if (inFlight.current || props.disabled) return;
            inFlight.current = true;
            setProcessing(true);
            try {
                const result = props.onClick?.(
                    {
                        actionId: props.actionId,
                        action: props.action,
                        data: props.data
                    },
                    reset
                );
                if (result && typeof result.then === 'function') {
                    // Promise-based row actions do not need to call the legacy done callback.
                    Promise.resolve(result).then(reset, reset);
                } else if (props.autoStop) {
                    autoStopTimer.current = setTimeout(reset, 1000);
                } else if (!props.onClick || props.onClick.length < 2) {
                    reset();
                }
            } catch (error) {
                reset();
                throw error;
            }
        },
        [props.onClick, props.autoStop, props.action, props.actionId, props.data, props.disabled, reset]
    );

    useImperativeHandle(ref, () => ({
        reset: reset
    }));

    const showProcessing = processing && !props.hideAnimation;

    return (
        <Button
            width={props.width === 'auto' ? undefined : (props.width ?? 120)}
            onClick={handleOnClick}
            disabled={props.disabled || processing}
            type={props.type ?? 'normal'}
            stylingMode={props.stylingMode ?? 'contained'}
            className="ant-processing-button"
        >
            <LoadIndicator className="ant-indicator-small" visible={showProcessing} />

            {props.icon && (
                <i
                    className={`dx-icon dx-icon-${props.icon}`}
                    style={{
                        marginRight: 2,
                        display: showProcessing ? 'none' : 'inline-block'
                    }}
                />
            )}
            <span
                className="dx-button-text"
                style={{
                    marginRight: showProcessing && props.waiting_text ? 9 : 0,
                    display: props.text || props.waiting_text ? 'inline' : 'none'
                }}
            >
                {showProcessing
                    ? AntLocalization.translateText(props.waiting_text || props.text)
                    : AntLocalization.translateText(props.text)}
            </span>
        </Button>
    );
});
